import { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function IvyLeagueVsRussellGroupPage() {
  const [ivyLeague, setIvyLeague] = useState([]);
  const [russellGroup, setRussellGroup] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usaRes, ukRes] = await Promise.all([
          axios.get(`${API}/universities?country=USA&limit=20`),
          axios.get(`${API}/universities?country=UK&limit=20`)
        ]);
        
        // Filter Ivy League schools
        const ivySchools = ['Harvard', 'Yale', 'Princeton', 'Columbia', 'Cornell', 'Dartmouth', 'Brown', 'Penn'];
        const ivy = usaRes.data.filter(uni => 
          ivySchools.some(school => uni.name.includes(school))
        ).sort((a, b) => a.ranking_global - b.ranking_global);
        
        // Get top Russell Group (UK elite)
        const russell = ukRes.data.sort((a, b) => a.ranking_global - b.ranking_global).slice(0, 8);
        
        setIvyLeague(ivy);
        setRussellGroup(russell);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="py-12 bg-white">
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto mb-12">
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-foreground mb-4">
            Ivy League vs Russell Group: Complete Comparison (2026)
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed mb-6">
            Compare America's prestigious Ivy League universities with the UK's elite Russell Group institutions. Understand the differences in admissions, costs, programs, and career outcomes.
          </p>
        </div>

        {/* Comparison Overview */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <div className="border-2 border-accent rounded-sm p-6 bg-accent/5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-accent rounded-sm flex items-center justify-center text-accent-foreground font-bold text-xl">
                🇺🇸
              </div>
              <h2 className="text-2xl font-playfair font-bold">Ivy League</h2>
            </div>
            <ul className="space-y-2 text-muted-foreground">
              <li>• <strong>Founded:</strong> 1636-1770s (Colonial era)</li>
              <li>• <strong>Number of Schools:</strong> 8 private universities</li>
              <li>• <strong>Degree Duration:</strong> 4 years undergraduate</li>
              <li>• <strong>Annual Cost:</strong> $55,000-$65,000</li>
              <li>• <strong>Financial Aid:</strong> Need-blind, full aid available</li>
              <li>• <strong>Known For:</strong> Holistic admissions, liberal arts</li>
            </ul>
          </div>

          <div className="border-2 border-primary rounded-sm p-6 bg-primary/5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-primary rounded-sm flex items-center justify-center text-primary-foreground font-bold text-xl">
                🇬🇧
              </div>
              <h2 className="text-2xl font-playfair font-bold">Russell Group</h2>
            </div>
            <ul className="space-y-2 text-muted-foreground">
              <li>• <strong>Founded:</strong> 1994 (as association)</li>
              <li>• <strong>Number of Schools:</strong> 24 research universities</li>
              <li>• <strong>Degree Duration:</strong> 3 years undergraduate</li>
              <li>• <strong>Annual Cost:</strong> £20,000-£38,000 (~$25k-$48k)</li>
              <li>• <strong>Financial Aid:</strong> Limited for international students</li>
              <li>• <strong>Known For:</strong> Specialized programs, research</li>
            </ul>
          </div>
        </div>

        {/* Head-to-Head Comparison */}
        <div className="mb-12">
          <h2 className="text-3xl font-playfair font-bold text-foreground mb-6">Key Differences</h2>
          <div className="space-y-4">
            <div className="border border-border/40 rounded-sm p-6">
              <h3 className="text-xl font-playfair font-bold mb-3">Academic Structure</h3>
              <div className="grid md:grid-cols-2 gap-4 text-muted-foreground">
                <div>
                  <strong className="text-foreground">Ivy League:</strong> Liberal arts focused, explore multiple subjects before declaring major, extensive general education requirements
                </div>
                <div>
                  <strong className="text-foreground">Russell Group:</strong> Specialized from day one, focus intensely on chosen subject, less flexibility to change majors
                </div>
              </div>
            </div>

            <div className="border border-border/40 rounded-sm p-6">
              <h3 className="text-xl font-playfair font-bold mb-3">Admissions Process</h3>
              <div className="grid md:grid-cols-2 gap-4 text-muted-foreground">
                <div>
                  <strong className="text-foreground">Ivy League:</strong> Holistic review, essays, extracurriculars, recommendations, SAT/ACT scores, interviews
                </div>
                <div>
                  <strong className="text-foreground">Russell Group:</strong> Academic focus, A-levels/IB scores, personal statement, less emphasis on extracurriculars
                </div>
              </div>
            </div>

            <div className="border border-border/40 rounded-sm p-6">
              <h3 className="text-xl font-playfair font-bold mb-3">Cost & Financial Aid</h3>
              <div className="grid md:grid-cols-2 gap-4 text-muted-foreground">
                <div>
                  <strong className="text-foreground">Ivy League:</strong> Higher sticker price but generous financial aid, many students pay less than $20k/year
                </div>
                <div>
                  <strong className="text-foreground">Russell Group:</strong> Lower overall cost, limited international scholarships, but shorter degree duration
                </div>
              </div>
            </div>

            <div className="border border-border/40 rounded-sm p-6">
              <h3 className="text-xl font-playfair font-bold mb-3">Campus Life</h3>
              <div className="grid md:grid-cols-2 gap-4 text-muted-foreground">
                <div>
                  <strong className="text-foreground">Ivy League:</strong> Residential colleges, Greek life, extensive campus activities, strong alumni networks
                </div>
                <div>
                  <strong className="text-foreground">Russell Group:</strong> Collegiate system (Oxbridge), pub culture, more independent living, rich history
                </div>
              </div>
            </div>

            <div className="border border-border/40 rounded-sm p-6">
              <h3 className="text-xl font-playfair font-bold mb-3">Career Outcomes</h3>
              <div className="grid md:grid-cols-2 gap-4 text-muted-foreground">
                <div>
                  <strong className="text-foreground">Ivy League:</strong> Average starting salary $70k-$90k, strong in finance, tech, consulting, OPT work authorization
                </div>
                <div>
                  <strong className="text-foreground">Russell Group:</strong> Average starting salary £25k-£35k (~$32k-$45k), strong in research, academia, EU opportunities
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* School Listings */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div>
            <h2 className="text-2xl font-playfair font-bold mb-6 flex items-center gap-2">
              <span className="text-3xl">🇺🇸</span> Ivy League Universities
            </h2>
            {loading ? (
              <div className="space-y-3">
                {[1,2,3].map(i => <div key={i} className="h-20 bg-secondary animate-pulse rounded-sm" />)}
              </div>
            ) : (
              <div className="space-y-3">
                {ivyLeague.map(uni => (
                  <Link key={uni.university_id} to={`/universities/${uni.university_id}`}>
                    <div className="border border-border/40 rounded-sm p-4 hover:shadow-md transition-all">
                      <div className="flex items-center gap-3">
                        <img src={uni.logo} alt={uni.name} className="w-10 h-10 rounded-sm object-cover" />
                        <div className="flex-1">
                          <h3 className="font-bold">{uni.name}</h3>
                          <p className="text-xs text-muted-foreground">{uni.region}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">Global Rank</div>
                          <div className="font-bold text-accent">#{uni.ranking_global}</div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div>
            <h2 className="text-2xl font-playfair font-bold mb-6 flex items-center gap-2">
              <span className="text-3xl">🇬🇧</span> Russell Group (Top 8)
            </h2>
            {loading ? (
              <div className="space-y-3">
                {[1,2,3].map(i => <div key={i} className="h-20 bg-secondary animate-pulse rounded-sm" />)}
              </div>
            ) : (
              <div className="space-y-3">
                {russellGroup.map(uni => (
                  <Link key={uni.university_id} to={`/universities/${uni.university_id}`}>
                    <div className="border border-border/40 rounded-sm p-4 hover:shadow-md transition-all">
                      <div className="flex items-center gap-3">
                        <img src={uni.logo} alt={uni.name} className="w-10 h-10 rounded-sm object-cover" />
                        <div className="flex-1">
                          <h3 className="font-bold">{uni.name}</h3>
                          <p className="text-xs text-muted-foreground">{uni.region}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">Global Rank</div>
                          <div className="font-bold text-primary">#{uni.ranking_global}</div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="bg-secondary rounded-sm p-8 text-center">
          <h2 className="text-2xl md:text-3xl font-playfair font-bold mb-4">Which Is Right for You?</h2>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Choose Ivy League if you want flexible curriculum, strong financial aid, and US career opportunities. Choose Russell Group for specialized education, shorter programs, and lower overall cost.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/study-usa">
              <Button className="rounded-sm">Explore US Universities</Button>
            </Link>
            <Link to="/study-europe">
              <Button variant="outline" className="rounded-sm">Explore UK Universities</Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}